/**
 * Duct Tool - BIM 2025 Grade
 * 
 * Comprehensive HVAC duct creation with:
 * - Automatic sizing based on airflow
 * - Pressure drop calculation
 * - Friction method (equal friction, static regain)
 * - Insulation/lining specification
 * - Duct system types (Supply, Return, Exhaust)
 * - Fitting loss coefficients
 * - Connector management
 * - Multi-segment routing
 */

import { v4 as uuidv4 } from 'uuid';
import { DuctElement, DuctFitting, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { ConnectorManager } from '../core/ConnectorManager';
import { MEPRoutingSolver } from '../core/MEPRoutingSolver';

// ==================== Duct System Types ====================

export type DuctSystemType =
    | 'Supply Air'
    | 'Return Air'
    | 'Exhaust Air'
    | 'Fresh Air'
    | 'Relief Air'
    | 'Other';

export type DuctSizingMethod =
    | 'Equal Friction'      // Constant friction loss per length
    | 'Static Regain'       // Maintain static pressure
    | 'Velocity Reduction'  // Reduce velocity along system
    | 'Fixed Size';         // Manual sizing

export type DuctShape = 'Rectangular' | 'Round' | 'Oval' | 'Flat Oval';

// ==================== Duct Material ====================

export interface DuctMaterial {
    id: string;
    name: string;
    roughness: number;      // mm - Absolute roughness
    gauge: number;          // Sheet metal gauge
    density: number;        // kg/m³ for weight calc
}

export const DUCT_MATERIALS: DuctMaterial[] = [
    { id: 'galv-steel', name: 'Galvanized Steel', roughness: 0.15, gauge: 22, density: 7850 },
    { id: 'aluminum', name: 'Aluminum', roughness: 0.05, gauge: 22, density: 2700 },
    { id: 'stainless', name: 'Stainless Steel', roughness: 0.05, gauge: 22, density: 8000 },
    { id: 'fiberglass', name: 'Fiberglass Duct Board', roughness: 0.9, gauge: 0, density: 48 },
    { id: 'flex', name: 'Flexible Duct', roughness: 3.0, gauge: 0, density: 50 },
];

// ==================== Duct Insulation ====================

export interface DuctInsulation {
    type: 'None' | 'Exterior' | 'Interior Lining' | 'Both';
    material: string;
    thickness: number;      // mm
    rValue: number;         // Thermal resistance (m²·K/W)
}

// ==================== Duct Sizing Tables ====================

// SMACNA standard sizes (mm)
export const RECTANGULAR_DUCT_WIDTHS = [100, 150, 200, 250, 300, 350, 400, 450, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800, 2000];
export const RECTANGULAR_DUCT_HEIGHTS = [100, 150, 200, 250, 300, 350, 400, 450, 500, 600];
export const ROUND_DUCT_DIAMETERS = [100, 125, 150, 175, 200, 225, 250, 275, 300, 350, 400, 450, 500, 550, 600, 700, 800, 900, 1000];

// ==================== Fitting Types ====================

export type DuctFittingType =
    | 'Elbow'
    | 'Tee'
    | 'Wye'
    | 'Transition'
    | 'Reducer'
    | 'Offset'
    | 'Takeoff'
    | 'Cap'
    | 'Damper'
    | 'Access Door';

export interface EnhancedDuctFitting extends DuctFitting {
    fittingType: DuctFittingType;
    lossCoefficient: number;    // K-factor
    pressureDrop: number;       // Pa

    // Connection info
    connectedDucts: string[];   // Element IDs

    // Geometry
    dimensions: {
        angle?: number;         // degrees for elbows
        radius?: number;        // mm for elbows
        inlet?: { width: number; height: number };
        outlet?: { width: number; height: number };
    };
}

// ==================== Enhanced Duct Geometry ====================

export interface EnhancedDuctGeometryData {
    // Route path
    path: [number, number, number][];

    // Cross-section
    shape: DuctShape;
    width: number;              // mm (or diameter for round)
    height: number;             // mm (0 for round)

    // Level reference
    referenceLevelId: string;
    offset: number;             // mm from level

    // Material and insulation
    materialId: string;
    insulation: DuctInsulation;

    // Fittings at connections
    fittings: EnhancedDuctFitting[];

    // Calculated values
    length: number;             // m
    equivalentDiameter: number; // mm - Hydraulic diameter
    area: number;               // m² - Cross-sectional area
    surfaceArea: number;        // m² - Total surface area
    weight: number;             // kg
}

// ==================== Enhanced Duct Element ====================

export interface EnhancedDuctElement extends DuctElement {
    geometryData: EnhancedDuctGeometryData;

    // System membership
    systemId?: string;
    systemType: DuctSystemType;

    // Airflow data
    airflow: {
        designFlow: number;     // L/s (or CFM)
        actualFlow?: number;    // L/s calculated
        velocity: number;       // m/s
        maxVelocity: number;    // m/s limit
    };

    // Pressure data
    pressure: {
        staticStart: number;    // Pa at segment start
        staticEnd: number;      // Pa at segment end
        frictionLoss: number;   // Pa per meter
        totalDrop: number;      // Pa total for segment
    };

    // Connectors
    connectors: {
        start: DuctConnector;
        end: DuctConnector;
    };
}

export interface DuctConnector {
    id: string;
    position: [number, number, number];
    direction: [number, number, number];
    shape: DuctShape;
    width: number;
    height: number;
    connectedTo?: string;       // Element ID
    flow: 'In' | 'Out' | 'Bidirectional';
}

// ==================== Tool Options ====================

export interface DuctToolOptions {
    // Shape and size
    shape: DuctShape;
    width: number;              // mm
    height: number;             // mm

    // System
    systemType: DuctSystemType;
    sizingMethod: DuctSizingMethod;

    // Design parameters
    designFlow: number;         // L/s
    maxVelocity: number;        // m/s
    frictionRate: number;       // Pa/m for equal friction

    // Material and insulation
    materialId: string;
    insulation: DuctInsulation;

    // Level
    referenceLevelId: string;
    offset: number;             // mm

    // Routing
    routing: 'Horizontal' | 'Vertical' | 'Sloped';
    slopePercent?: number;

    // Chain placement
    chainMode: boolean;
    autoFittings: boolean;
}

// ==================== Duct Tool Class ====================

export class DuctTool extends BaseTool {
    id = 'duct';
    name = 'Duct';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private lastCreatedDuct: EnhancedDuctElement | null = null;
    private routePoints: THREE.Vector3[] = [];

    private options: DuctToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): DuctToolOptions {
        return {
            shape: 'Rectangular',
            width: 400,
            height: 300,
            systemType: 'Supply Air',
            sizingMethod: 'Equal Friction',
            designFlow: 500,            // L/s
            maxVelocity: 8,             // m/s
            frictionRate: 1.0,          // Pa/m
            materialId: 'galv-steel',
            insulation: {
                type: 'None',
                material: '',
                thickness: 0,
                rValue: 0
            },
            referenceLevelId: 'Level 1',
            offset: 3000,               // 3m above level
            routing: 'Horizontal',
            chainMode: true,
            autoFittings: true
        };
    }

    /**
     * Set duct options from UI
     */
    setOptions(options: Partial<DuctToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): DuctToolOptions {
        return { ...this.options };
    }

    /**
     * Auto-size duct based on airflow
     */
    autoSize(airflowLps: number): { width: number; height: number } {
        const velocity = Math.min(this.options.maxVelocity, 8); // m/s max
        const areaRequired = (airflowLps / 1000) / velocity; // m²

        if (this.options.shape === 'Round') {
            // A = π * d² / 4
            const diameter = Math.sqrt(4 * areaRequired / Math.PI) * 1000;
            const nearestDia = this.findNearestSize(diameter, ROUND_DUCT_DIAMETERS);
            return { width: nearestDia, height: nearestDia };
        } else {
            // Rectangular: try to find best aspect ratio
            const targetAspect = 1.5; // width/height
            const targetHeight = Math.sqrt(areaRequired / targetAspect) * 1000;
            const targetWidth = targetHeight * targetAspect;

            const height = this.findNearestSize(targetHeight, RECTANGULAR_DUCT_HEIGHTS);
            const width = this.findNearestSize(targetWidth, RECTANGULAR_DUCT_WIDTHS);

            return { width, height };
        }
    }

    private findNearestSize(value: number, sizes: number[]): number {
        return sizes.reduce((prev, curr) =>
            Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
        );
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        let snapped = this.snapToGrid(point);

        // Snap to connectors
        const elements = Array.from(this.context.bimStore.project.elements.values());
        const connector = ConnectorManager.findClosestConnector(point, elements);
        if (connector) {
            snapped = connector.position.clone();
        }

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
            this.routePoints = [snapped];
        } else {
            // Validate segment
            if (snapped.distanceTo(this.startPoint) < 0.01) return;

            // Create duct segment
            const newDuct = this.createDuct(this.startPoint, snapped);

            if (newDuct) {
                this.context.bimStore.addElement(newDuct);

                // Auto-create fitting if changing direction
                if (this.lastCreatedDuct && this.options.autoFittings) {
                    const fitting = this.createFitting(this.lastCreatedDuct, newDuct, this.startPoint);
                    if (fitting) {
                        this.context.bimStore.addElement(fitting);
                    }
                }

                this.lastCreatedDuct = newDuct;
            }

            if (this.options.chainMode) {
                this.startPoint = snapped;
                this.routePoints.push(snapped);
            } else {
                this.startPoint = null;
                this.currentPoint = null;
                this.routePoints = [];
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            let snapped = this.snapToGrid(point);

            const elements = Array.from(this.context.bimStore.project.elements.values());
            const connector = ConnectorManager.findClosestConnector(point, elements);
            if (connector) {
                snapped = connector.position.clone();
            }

            // Constrain to routing mode
            if (this.startPoint && this.options.routing === 'Horizontal') {
                snapped.z = this.startPoint.z;
            }

            this.currentPoint = snapped;
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.lastCreatedDuct = null;
                this.routePoints = [];
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        } else if (event.key === 'Enter') {
            // Finish route
            this.startPoint = null;
            this.currentPoint = null;
            this.lastCreatedDuct = null;
            this.routePoints = [];
            this.context?.appStore.setToolPreview(null);
        } else if (event.key === 's' || event.key === 'S') {
            // Toggle auto-size
            if (this.options.sizingMethod !== 'Fixed Size') {
                const size = this.autoSize(this.options.designFlow);
                this.setOptions({ width: size.width, height: size.height });
            }
        }
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            // Calculate pressure drop for preview
            const length = this.startPoint.distanceTo(this.currentPoint);
            const pressureDrop = this.calculatePressureDrop(length);

            this.context.appStore.setToolPreview({
                type: 'duct',
                start: this.startPoint,
                end: this.currentPoint,
                shape: this.options.shape,
                width: this.options.width / 1000,
                height: this.options.height / 1000,
                systemType: this.options.systemType,
                pressureDrop: pressureDrop.toFixed(1) + ' Pa'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createDuct(start: THREE.Vector3, end: THREE.Vector3): EnhancedDuctElement | null {
        if (!this.context) return null;

        const length = start.distanceTo(end);
        if (length < 0.01) return null;

        // Calculate hydraulic values
        const area = this.calculateArea();
        const equivalentDiameter = this.calculateEquivalentDiameter();
        const velocity = (this.options.designFlow / 1000) / area;
        const frictionLoss = this.calculateFrictionRate(velocity, equivalentDiameter);
        const totalDrop = frictionLoss * length;
        const surfaceArea = this.calculateSurfaceArea(length);
        const weight = this.calculateWeight(length, surfaceArea);

        // Create connectors
        const startConnector: DuctConnector = {
            id: uuidv4(),
            position: start.toArray() as [number, number, number],
            direction: end.clone().sub(start).normalize().toArray() as [number, number, number],
            shape: this.options.shape,
            width: this.options.width,
            height: this.options.height,
            flow: 'In'
        };

        const endConnector: DuctConnector = {
            id: uuidv4(),
            position: end.toArray() as [number, number, number],
            direction: start.clone().sub(end).normalize().toArray() as [number, number, number],
            shape: this.options.shape,
            width: this.options.width,
            height: this.options.height,
            flow: 'Out'
        };

        const geometryData: EnhancedDuctGeometryData = {
            path: [start.toArray() as [number, number, number], end.toArray() as [number, number, number]],
            shape: this.options.shape,
            width: this.options.width,
            height: this.options.height,
            referenceLevelId: this.options.referenceLevelId,
            offset: this.options.offset,
            materialId: this.options.materialId,
            insulation: { ...this.options.insulation },
            fittings: [],
            length,
            equivalentDiameter,
            area,
            surfaceArea,
            weight
        };

        const duct: EnhancedDuctElement = {
            id: uuidv4(),
            category: 'Ducts',
            type: `${this.options.shape} Duct`,
            parameters: this.buildParameters(geometryData, velocity, frictionLoss, totalDrop),
            geometryData,
            constraints: this.buildConstraints(),
            systemType: this.options.systemType,
            airflow: {
                designFlow: this.options.designFlow,
                velocity: Math.round(velocity * 100) / 100,
                maxVelocity: this.options.maxVelocity
            },
            pressure: {
                staticStart: 0,
                staticEnd: 0,
                frictionLoss: Math.round(frictionLoss * 100) / 100,
                totalDrop: Math.round(totalDrop * 10) / 10
            },
            connectors: {
                start: startConnector,
                end: endConnector
            }
        };

        return duct;
    }

    private calculateArea(): number {
        if (this.options.shape === 'Round') {
            return Math.PI * Math.pow(this.options.width / 2000, 2);
        } else {
            return (this.options.width / 1000) * (this.options.height / 1000);
        }
    }

    private calculateEquivalentDiameter(): number {
        if (this.options.shape === 'Round') {
            return this.options.width;
        } else {
            // Hydraulic diameter Dh = 4A/P
            const w = this.options.width;
            const h = this.options.height;
            return (2 * w * h) / (w + h);
        }
    }

    private calculateFrictionRate(velocity: number, equivalentDiameter: number): number {
        // Darcy-Weisbach equation: ΔP/L = f * ρ * v² / (2 * Dh)
        // Using Colebrook-White for friction factor

        const material = DUCT_MATERIALS.find(m => m.id === this.options.materialId) || DUCT_MATERIALS[0];
        const roughness = material.roughness / 1000; // Convert to meters
        const diameter = equivalentDiameter / 1000; // Convert to meters
        const rho = 1.2; // Air density kg/m³
        const mu = 1.81e-5; // Dynamic viscosity Pa·s

        // Reynolds number
        const Re = (rho * velocity * diameter) / mu;

        // Friction factor (simplified Swamee-Jain approximation)
        const term1 = roughness / (3.7 * diameter);
        const term2 = 5.74 / Math.pow(Re, 0.9);
        const f = 0.25 / Math.pow(Math.log10(term1 + term2), 2);

        // Pressure drop per meter
        const frictionLoss = f * (rho * velocity * velocity) / (2 * diameter);

        return frictionLoss;
    }

    private calculatePressureDrop(length: number): number {
        const area = this.calculateArea();
        const velocity = (this.options.designFlow / 1000) / area;
        const equivalentDiameter = this.calculateEquivalentDiameter();
        const frictionLoss = this.calculateFrictionRate(velocity, equivalentDiameter);
        return frictionLoss * length;
    }

    private calculateSurfaceArea(length: number): number {
        if (this.options.shape === 'Round') {
            return Math.PI * (this.options.width / 1000) * length;
        } else {
            const perimeter = 2 * ((this.options.width / 1000) + (this.options.height / 1000));
            return perimeter * length;
        }
    }

    private calculateWeight(length: number, surfaceArea: number): number {
        const material = DUCT_MATERIALS.find(m => m.id === this.options.materialId) || DUCT_MATERIALS[0];

        // Sheet thickness from gauge (approximate)
        const gaugeThickness: Record<number, number> = {
            22: 0.76, 24: 0.61, 26: 0.46
        };
        const thickness = gaugeThickness[material.gauge] || 0.76;

        return surfaceArea * (thickness / 1000) * material.density;
    }

    private createFitting(duct1: EnhancedDuctElement, duct2: EnhancedDuctElement, connectionPoint: THREE.Vector3): EnhancedDuctElement | null {
        // Calculate angle between ducts
        const path1 = duct1.geometryData.path;
        const path2 = duct2.geometryData.path;

        const dir1 = new THREE.Vector3(
            path1[1][0] - path1[0][0],
            path1[1][1] - path1[0][1],
            path1[1][2] - path1[0][2]
        ).normalize();

        const dir2 = new THREE.Vector3(
            path2[1][0] - path2[0][0],
            path2[1][1] - path2[0][1],
            path2[1][2] - path2[0][2]
        ).normalize();

        const angle = Math.acos(Math.abs(dir1.dot(dir2))) * (180 / Math.PI);

        // Only create fitting if angle is significant
        if (angle < 10) return null;

        const fittingData = MEPRoutingSolver.createElbow(duct1 as any, duct2 as any, connectionPoint);
        return fittingData as EnhancedDuctElement | null;
    }

    private buildParameters(
        geom: EnhancedDuctGeometryData,
        velocity: number,
        frictionLoss: number,
        totalDrop: number
    ): Record<string, any> {
        return {
            // Dimensions
            'Width': geom.width,
            'Height': geom.shape === 'Round' ? geom.width : geom.height,
            'Length': Math.round(geom.length * 1000) / 1000,
            'Equivalent Diameter': Math.round(geom.equivalentDiameter),
            'Area': Math.round(geom.area * 10000) / 10000,

            // Airflow
            'Flow': this.options.designFlow,
            'Velocity': Math.round(velocity * 100) / 100,

            // Pressure
            'Friction': Math.round(frictionLoss * 100) / 100,
            'Pressure Drop': Math.round(totalDrop * 10) / 10,

            // Physical
            'System Type': this.options.systemType,
            'Material': this.options.materialId,
            'Insulation Type': geom.insulation.type,
            'Weight': Math.round(geom.weight * 10) / 10,
            'Surface Area': Math.round(geom.surfaceArea * 100) / 100,

            // Level
            'Reference Level': geom.referenceLevelId,
            'Offset': geom.offset
        };
    }

    protected buildConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.referenceLevelId,
                data: {
                    offset: this.options.offset
                }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Calculate system pressure for a connected duct run
 */
export function calculateSystemPressure(ducts: EnhancedDuctElement[]): number {
    let totalPressure = 0;

    for (const duct of ducts) {
        totalPressure += duct.pressure.totalDrop;

        // Add fitting losses
        for (const fitting of duct.geometryData.fittings) {
            totalPressure += fitting.pressureDrop;
        }
    }

    return totalPressure;
}

/**
 * Get fitting loss coefficient (K-factor)
 */
export function getFittingLossCoefficient(fittingType: DuctFittingType, angle: number = 90): number {
    const kFactors: Record<DuctFittingType, number> = {
        'Elbow': angle === 90 ? 0.22 : (angle === 45 ? 0.12 : 0.05 * angle / 90),
        'Tee': 1.0,
        'Wye': 0.6,
        'Transition': 0.1,
        'Reducer': 0.15,
        'Offset': 0.3,
        'Takeoff': 0.8,
        'Cap': 0.5,
        'Damper': 0.2,
        'Access Door': 0.1
    };

    return kFactors[fittingType] || 0.2;
}
